import React from 'react';
import { createPortal } from 'react-dom';
import { Check } from 'lucide-react';

interface DrivePeriodDropdownProps {
  theme: ReturnType<typeof import('../../contexts/ThemeContext').useTheme>['theme'];
  isOpen: boolean;
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  menuRef: React.RefObject<HTMLDivElement>;
  uniquePeriods: string[];
  currentPeriod: string;
  onSelectPeriod: (period: string) => void;
  onClose: () => void;
}

const DrivePeriodDropdown: React.FC<DrivePeriodDropdownProps> = ({
  theme,
  isOpen,
  anchorRef,
  menuRef,
  uniquePeriods,
  currentPeriod,
  onSelectPeriod,
  onClose,
}) => {
  if (!isOpen || !anchorRef.current) {
    return null;
  }

  const style = (() => {
    const buttonRect = anchorRef.current!.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const estimatedHeight = 300;
    const spaceBelow = viewportHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;
    const shouldOpenUp = spaceBelow < estimatedHeight && spaceAbove > spaceBelow;

    return {
      [shouldOpenUp ? 'bottom' : 'top']: shouldOpenUp
        ? `${viewportHeight - buttonRect.top + 4}px`
        : `${buttonRect.bottom + 4}px`,
      left: `${buttonRect.left}px`,
    };
  })();

  const handleSelect = (period: string) => {
    onSelectPeriod(period);
    onClose();
  };

  return createPortal(
    <div
      ref={menuRef}
      className={`fixed ${theme.colors.bg.secondary} border ${theme.colors.border.primary} rounded-lg shadow-lg z-[9999] py-1 min-w-[180px]`}
      onClick={(e) => e.stopPropagation()}
      style={style}
    >
      <div className="px-2 py-1">
        <div className={`text-xs ${theme.colors.text.secondary} px-2 py-1`}>Período</div>
        <button
          onClick={() => handleSelect('all')}
          className={`w-full text-left px-3 py-1.5 text-sm rounded flex items-center justify-between hover:${
            theme.colors.bg.tertiary
          } transition-colors ${
            currentPeriod === 'all' ? theme.colors.text.primary : theme.colors.text.secondary
          }`}
        >
          <span>Todos los periodos</span>
          {currentPeriod === 'all' && <Check className={`w-4 h-4 ${theme.colors.text.primary}`} />}
        </button>
        {uniquePeriods.map((period) => (
          <button
            key={period}
            onClick={() => handleSelect(period)}
            className={`w-full text-left px-3 py-1.5 text-sm rounded flex items-center justify-between hover:${
              theme.colors.bg.tertiary
            } transition-colors ${
              currentPeriod === period ? theme.colors.text.primary : theme.colors.text.secondary
            }`}
          >
            <span>{period}</span>
            {currentPeriod === period && (
              <Check className={`w-4 h-4 ${theme.colors.text.primary}`} />
            )}
          </button>
        ))}
      </div>
    </div>,
    document.body
  );
};

export default DrivePeriodDropdown;
